import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/utils'

const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default:
          'bg-primary text-white hover:bg-primary-700 active:bg-primary-800',
        destructive:
          'bg-error text-white hover:bg-[#b91c1c] active:bg-[#991b1b]',
        outline:
          'border border-[#e7e5e4] bg-white text-charcoal hover:bg-[#f5f5f4] active:bg-[#e7e5e4]',
        secondary:
          'bg-[#f5f5f4] text-charcoal hover:bg-[#e7e5e4] active:bg-[#d6d3d1]',
        ghost:
          'hover:bg-[#f5f5f4] hover:text-charcoal active:bg-[#e7e5e4]',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-9 rounded-md px-3',
        lg: 'h-11 rounded-md px-8',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  /**
   * Accessibility label for screen readers
   * Use for icon-only buttons or when button text needs explanation
   * @example aria-label="Send message"
   */
  'aria-label'?: string
  /**
   * Tooltip text displayed on hover
   * Use alongside aria-label for icon buttons
   * @example title="Send message"
   */
  title?: string
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = 'Button'

export { Button, buttonVariants }
